import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AccessibilityProvider, useAccessibility } from './accessibility/AccessibilityContext.js';
import { PublicShell } from './components/PublicShell.js';
import { AccessibilityPage } from './pages/AccessibilityPage.js';
import { CartPage } from './pages/CartPage.js';
import { CatalogPage } from './pages/CatalogPage.js';
import { ContactPage } from './pages/ContactPage.js';
import { GalleryPage } from './pages/GalleryPage.js';
import { HomePage } from './pages/HomePage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { ProductPage } from './pages/ProductPage.js';
import { RequestPage } from './pages/RequestPage.js';
import { TermsPage } from './pages/TermsPage.js';
import { ThankYouPage } from './pages/ThankYouPage.js';
import { CartProvider } from './cart/CartContext.js';
import { CookieConsentProvider } from './consent/CookieConsentContext.js';
import { AuditLogPage } from './admin/AuditLogPage.js';
import { CategoriesPage } from './admin/CategoriesPage.js';
import { CustomerDetailPage } from './admin/CustomerDetailPage.js';
import { CustomersPage } from './admin/CustomersPage.js';
import { DashboardPage } from './admin/DashboardPage.js';
import { LoginPage } from './admin/LoginPage.js';
import { ManualQuotePage } from './admin/ManualQuotePage.js';
import { ProductFormPage } from './admin/ProductFormPage.js';
import { ProductsPage } from './admin/ProductsPage.js';
import { QuoteDetailPage } from './admin/QuoteDetailPage.js';
import { QuotesPage } from './admin/QuotesPage.js';
import { RequireAuth } from './admin/RequireAuth.js';
import './index.css';

/**
 * ⭐ TASKS 4.6 — מחבר את העדפת "עצירת אנימציות" (`AccessibilityContext`) ל-
 *    `MotionConfig` עצמו, ⛔ לא רק ל-CSS. אנימציות שמריצה `motion` (למשל
 *    ה-Hero, `HomePage.tsx`) בנויות על ה-Web Animations API/JS, ⛔ לא על
 *    CSS transitions — לכן חסימת CSS גרידא (`index.css`,
 *    `[data-a11y-stop-animations]`) לא הייתה עוצרת אותן. `reducedMotion="always"`
 *    הוא הדרך הרשמית של הספרייה לכבות אנימציה תכנותית, בדיוק כמו
 *    `reducedMotion="user"` שכבר קיים ל-`prefers-reduced-motion`.
 */
function MotionConfigConnected({ children }: { children: ReactNode }) {
  const { prefs } = useAccessibility();
  return (
    <MotionConfig reducedMotion={prefs.stopAnimations ? 'always' : 'user'}>{children}</MotionConfig>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('אלמנט #root לא נמצא ב-index.html');

createRoot(container).render(
  <StrictMode>
    {/* ⭐ TASKS 4.6 — עוטף הכל: ה-`html[data-a11y-*]` חייב להיות זמין לפני
        שכל שאר האפליקציה עולה, וה-widget עצמו (Layout.tsx) קורא ל-
        useAccessibility. */}
    <AccessibilityProvider>
      <MotionConfigConnected>
        <BrowserRouter>
          {/* ⭐ TASKS 4.2 — עגלת הבקשות (ראו cart/CartContext.tsx). עוטף גם את
              פאנל הניהול בלי נזק — הוא פשוט לא קורא ל-useCart. */}
          {/* ⭐ הסכמת עוגיות (27.08.2026) — עוטף את הכל כדי שה-Footer וה-Layout
              יוכלו לקרוא ל-useCookieConsent. הבאנר עצמו מרונדר רק בצד הציבורי
              (Layout.tsx), בדיוק כמו AccessibilityWidget. */}
          <CookieConsentProvider>
            <CartProvider>
              <Routes>
                {/* ⭐ צד ציבורי — TASKS 4.1. ⛔ אין login/אימות כאן, ראו CLAUDE.md:
                "אין הרשמה, אין התחברות, אין אזור אישי ללקוחות קצה".
                ⭐ TASKS 6.2 · DESIGN.md 6.5 — `PublicShell` הוא route layout
                חסר-path: עוטף את כל הנתיבים למטה ב-`Layout` + `AnimatePresence`
                אחת ויחידה, ⛔ בלי לשנות אף כתובת. Header/Footer נשארים קבועים
                מעל המעברים במקום להירכב מחדש בכל ניווט. */}
                <Route element={<PublicShell />}>
                  <Route path="/" element={<HomePage />} />
                  {/* ⭐ Feedback1.md סעיף 7 (20.08.2026) — "אודות" מוזג לתוך דף
                    הגלריה החדש. `/about` הופך ל-redirect (0.7, מגן על
                    קישורים שכבר אונדקסו), בדיוק כמו `/calculator` למטה. */}
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/about" element={<Navigate to="/gallery" replace />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />

                  {/* ⭐ הקטלוג — TASKS 4.2 · Feedback1.md סעיף 8 (20.08.2026): קטלוג
                    שטוח, בלי ניווט קטגוריות. `/catalog/:categorySlug` (דף
                    הקטגוריה הישן) יורד ומפנה ל-`/catalog` — מגן על קישורים
                    שכבר אונדקסו, בדיוק כמו redirect המחשבון (סעיף 5). מבנה
                    ה-URL של דף מוצר עצמו **נשאר** `/catalog/:categorySlug/:sku`
                    (הוחלט 0.5, מגן על קישורים שכבר אונדקסו). */}
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route
                    path="/catalog/:categorySlug"
                    element={<Navigate to="/catalog" replace />}
                  />
                  <Route path="/catalog/:categorySlug/:sku" element={<ProductPage />} />
                  {/* ⭐ Feedback1.md סעיף 5 (19.08.2026) — מחשבון מ"ר הוסר לגמרי
                    בבקשת הלקוח. redirect ל-/catalog מונע דף שבור לגולש שהגיע
                    מקישור/תוצאת חיפוש ישנה. */}
                  <Route path="/calculator" element={<Navigate to="/catalog" replace />} />

                  {/* ⭐ עגלה + טופס בקשה — TASKS 4.3–4.4 */}
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/request" element={<RequestPage />} />
                  <Route path="/request/thank-you" element={<ThankYouPage />} />
                </Route>

                {/* ⭐ פאנל הניהול — משתמש יחיד: טל (TASKS 3.1) */}
                <Route path="/admin/login" element={<LoginPage />} />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <DashboardPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/categories"
                  element={
                    <RequireAuth>
                      <CategoriesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/products"
                  element={
                    <RequireAuth>
                      <ProductsPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/products/new"
                  element={
                    <RequireAuth>
                      <ProductFormPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/products/:id/edit"
                  element={
                    <RequireAuth>
                      <ProductFormPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/audit-log"
                  element={
                    <RequireAuth>
                      <AuditLogPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <RequireAuth>
                      <CustomersPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/customers/:id"
                  element={
                    <RequireAuth>
                      <CustomerDetailPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/quotes"
                  element={
                    <RequireAuth>
                      <QuotesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/quotes/new"
                  element={
                    <RequireAuth>
                      <ManualQuotePage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/quotes/:id"
                  element={
                    <RequireAuth>
                      <QuoteDetailPage />
                    </RequireAuth>
                  }
                />
              </Routes>
            </CartProvider>
          </CookieConsentProvider>
        </BrowserRouter>
      </MotionConfigConnected>
    </AccessibilityProvider>
  </StrictMode>,
);
